package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import com.heelion.farmafreelance.agent.RispostaAgente
import com.heelion.farmafreelance.voice.VoiceIntent
import com.heelion.farmafreelance.voice.rememberVoiceRecognizer

/**
 * Pulsante di ricerca a voce, riusabile ovunque nell'app. Come [VoiceFillButton] in
 * `DisponibilitaScreen.kt`: riconoscimento vocale locale (nessun costo), parser locale,
 * nessuna chiamata inviata da sola — passa solo l'intento riconosciuto al chiamante, che decide
 * come applicarlo ai propri filtri di ricerca esistenti.
 *
 * Il risultato ("Ho sentito: ...") resta scritto sotto il pulsante finché non si riprova (non
 * uno Snackbar che sparisce da solo), e la pagina scorre automaticamente fino a qui quando il
 * microfono ha finito di ascoltare, così il risultato è sempre visibile.
 *
 * Non visibile se il riconoscimento vocale non è disponibile sulla piattaforma corrente
 * (Desktop, Wasm per ora): la ricerca via tastiera/filtri resta l'unico modo, come oggi.
 *
 * @param onIntent applica l'intento riconosciuto ai filtri del chiamante e restituisce il
 *   messaggio da mostrare sotto il pulsante (es. "Ho cercato in base a quello che ho capito").
 */
@OptIn(androidx.compose.foundation.ExperimentalFoundationApi::class)
@Composable
fun VoiceSearchButton(
    hint: String,
    onIntent: (rawText: String, intent: VoiceIntent) -> String,
    onError: (String) -> Unit
) {
    val voiceRecognizer = rememberVoiceRecognizer()
    if (!voiceRecognizer.isAvailable()) return
    val assistente = LocalAssistente.current
    var isListening by remember { mutableStateOf(false) }
    var voiceFeedback by remember { mutableStateOf<String?>(null) }
    val bringIntoViewRequester = remember { BringIntoViewRequester() }
    val scope = rememberCoroutineScope()

    Column(modifier = Modifier.bringIntoViewRequester(bringIntoViewRequester)) {
        OutlinedButton(
            onClick = {
                // Zitto prima di ascoltare: il microfono non deve sentire la voce dell'assistente.
                assistente.voce.stop()
                isListening = true
                voiceRecognizer.startListening(
                    onResult = { text ->
                        isListening = false
                        val esecutore = assistente.esecutore
                        // "sì"/"no" a un'azione di Farmy in attesa di conferma.
                        if (esecutore != null && esecutore.eRispostaConferma(text)) {
                            scope.launch {
                                val e = esecutore.rispondiConferma(text)
                                voiceFeedback = e.testo
                                assistente.voce.speak(e.testo)
                            }
                            return@startListening
                        }
                        esecutore?.dimenticaAzioneInAttesa()
                        val esito = assistente.elabora(text)
                        println("--- [VOICE] Testo: \"$text\" -> $esito ---")
                        voiceFeedback = when (esito) {
                            // Le domande hanno risposta qui, senza toccare filtri o sezione:
                            // chi ha chiesto a voce riceve la risposta anche a voce.
                            is RispostaAgente.Domanda -> {
                                scope.launch {
                                    val r = assistente.rispondi(esito.tipo, text)
                                    voiceFeedback = r
                                    assistente.voce.speak(r)
                                }
                                "Sto controllando…"
                            }
                            is RispostaAgente.Comando -> onIntent(text, esito.intent)
                            is RispostaAgente.Azione -> {
                                scope.launch {
                                    val e = esecutore?.prepara(esito.azione) ?: EsitoAzione("Non posso farlo da qui.")
                                    voiceFeedback = e.testo
                                    assistente.voce.speak(e.testo)
                                }
                                "Sto controllando…"
                            }
                            is RispostaAgente.ImpostaPromemoria ->
                                assistente.impostaPromemoria(esito.impostazione).also { assistente.voce.speak(it) }
                            is RispostaAgente.Chiacchiera ->
                                assistente.chiacchiera(esito.tipo).first.also { assistente.voce.speak(it) }
                            is RispostaAgente.NonCapito -> {
                                assistente.voce.speak("Scusami, non ho capito. Prova a dirlo con altre parole.")
                                onIntent(text, VoiceIntent.Unrecognized(text))
                            }
                        }
                        scope.launch { bringIntoViewRequester.bringIntoView() }
                    },
                    onError = { msg ->
                        isListening = false
                        voiceFeedback = msg
                        onError(msg)
                        scope.launch { bringIntoViewRequester.bringIntoView() }
                    }
                )
            },
            enabled = !isListening,
            modifier = Modifier.fillMaxWidth()
        ) {
            // Sempre rosso per farsi notare; mentre ascolta pulsa.
            val pulsa = rememberLampeggio(isListening)
            Icon(AppIcons.Mic, contentDescription = null, tint = Color(0xFFD32F2F).copy(alpha = 1f - 0.6f * pulsa))
            Spacer(Modifier.width(6.dp))
            Text(text = if (isListening) "In ascolto..." else hint, fontSize = 11.sp)
        }
        if (voiceFeedback != null) {
            Text(
                text = voiceFeedback!!,
                style = MaterialTheme.typography.labelSmall,
                color = Color.Gray,
                modifier = Modifier.padding(top = 4.dp)
            )
        }
    }
}
